<template>
  <div class="c2c-trim">
    <button type="button" class="c2c-trim-toggle" @click="open = !open" :aria-expanded="open">
      <component :is="open ? ChevronUp : ChevronDown" :size="16" />
      <span>{{ t('trim.toggle_label') }}</span>
      <span v-if="trimActive" class="c2c-trim-badge">
        {{ t('trim.badge_active', { duration: formatDuration(outputDuration) }) }}
      </span>
      <span v-else-if="overLimit" class="c2c-trim-badge c2c-trim-badge--warn">
        {{ t('trim.badge_recommended') }}
      </span>
    </button>

    <div v-if="open" class="c2c-trim-body">
      <p class="c2c-trim-help">{{ t('trim.help') }}</p>

      <div class="c2c-trim-preview" v-if="objectUrl">
        <div class="c2c-trim-stage">
          <!-- Video player with full controls -->
          <video
            ref="videoEl"
            class="c2c-trim-video"
            :class="{ 'c2c-trim-video--hidden': isAudioOnly }"
            :src="objectUrl"
            :muted="isMuted"
            preload="metadata"
            playsinline
            @loadedmetadata="onVideoMeta"
            @error="onVideoError"
            @timeupdate="onTimeUpdate"
            @play="isPlaying = true"
            @pause="isPlaying = false"
            @ended="isPlaying = false"
          ></video>

          <!--
            Audio-only hides the picture at encode time, so the preview has
            to stop showing one. Covering the frame rather than unmounting
            the element keeps playback, seeking and the controls below
            working on the audio track exactly as before.
          -->
          <div v-if="isAudioOnly" class="c2c-audio-poster">
            <span class="c2c-audio-poster-icon" aria-hidden="true">
              <AudioLines :size="34" />
            </span>
            <strong>{{ t('trim.audio_poster_title') }}</strong>
            <small>{{ t('trim.audio_poster_sub') }}</small>
          </div>
        </div>

        <p v-if="previewError" class="c2c-trim-preview-error">{{ previewError }}</p>

        <!-- Custom control bar -->
        <div v-if="!previewError" class="c2c-video-controls">
          <button
            type="button" class="c2c-ctrl-btn"
            @click="togglePlay"
            :title="isPlaying ? t('trim.pause') : t('trim.play')"
            :aria-label="isPlaying ? t('trim.pause') : t('trim.play')"
          >
            <component :is="isPlaying ? Pause : Play" :size="16" />
          </button>

          <button
            type="button" class="c2c-ctrl-btn"
            @click="playSegment"
            :title="t('trim.play_segment')"
            :aria-label="t('trim.play_segment_aria')"
          >
            <Scissors :size="16" />
          </button>

<!-- Seek bar: rail and played progress underneath, then the input on
               top with everything but its thumb made transparent. While a
               trim is set the bar spans the trimmed segment only, so the
               preview cannot wander outside what will be uploaded. -->
          <div class="c2c-seek-wrap">
            <div class="c2c-seek-rail"></div>
            <div class="c2c-seek-played" :style="seekPlayedStyle"></div>
            <input
              type="range"
              class="c2c-seek-bar"
              :min="seekMin"
              :max="seekMax"
              step="any"
              :value="currentTime"
              :aria-label="t('trim.seek_aria')"
              :aria-valuetext="clock(currentTime)"
              @input="onSeekInput"
            />
          </div>

          <span class="c2c-time-display">{{ playerTime }}</span>

          <!-- Mute. Locked while the output has no audio track: offering to
               unmute something that will be uploaded silent is a lie. -->
          <button
            type="button" class="c2c-ctrl-btn"
            :class="{ 'c2c-ctrl-btn--locked': isVideoOnly }"
            :disabled="isVideoOnly"
            @click="toggleMute"
            :title="isVideoOnly ? t('trim.muted_video_only') : (isMuted ? t('trim.unmute') : t('trim.mute'))"
            :aria-label="isVideoOnly ? t('trim.muted_video_only') : (isMuted ? t('trim.unmute') : t('trim.mute'))"
          >
            <component :is="isMuted ? VolumeX : Volume2" :size="16" />
          </button>
        </div>
      </div>

      <fieldset class="c2c-edit-audio-row">
        <!-- Kept for screen readers; on screen the three options speak
             for themselves and the heading was just noise. -->
        <legend class="c2c-edit-audio-legend c2c-visually-hidden">
          {{ t('trim.audio_group_label') }}
        </legend>
        <label class="c2c-edit-audio-opt">
          <input
            type="radio"
            name="audio-mode"
            :checked="props.mediaMode !== 'video-only' && props.mediaMode !== 'audio-only'"
            @change="emit('update:mediaMode', 'video-audio')"
          />
          {{ t('trim.audio_video_and_audio') }}
        </label>
        <label class="c2c-edit-audio-opt">
          <input
            type="radio"
            name="audio-mode"
            :checked="props.mediaMode === 'video-only'"
            @change="emit('update:mediaMode', 'video-only')"
          />
          {{ t('trim.audio_video_only') }}
        </label>
        <label
          class="c2c-edit-audio-opt"
          :class="{ 'c2c-edit-audio-opt--disabled': !props.hasAudio }"
        >
          <input
            type="radio"
            name="audio-mode"
            :disabled="!props.hasAudio"
            :checked="props.mediaMode === 'audio-only'"
            @change="emit('update:mediaMode', 'audio-only')"
          />
          {{ t('trim.audio_audio_only') }}
        </label>
      </fieldset>
      <p v-if="!props.hasAudio" class="c2c-edit-audio-note">
        {{ t('trim.audio_none_in_source') }}
      </p>
      <p v-else-if="isAudioOnly" class="c2c-edit-audio-note">
        {{ t('trim.audio_only_note') }}
      </p>
      <p v-else-if="isVideoOnly" class="c2c-edit-audio-note">
        {{ t('trim.muted_video_only') }}
      </p>

      <label class="c2c-trim-enable">
        <input type="checkbox" v-model="trimEnabled" />
        <span>{{ t('trim.enable_label') }}</span>
      </label>

      <div v-if="trimEnabled" class="c2c-trim-editor">
        <!--
          One timeline, two handles. Two native range inputs stacked over the
          same track: keyboard support, focus and screen-reader semantics come
          free, which a pointer-only drag widget would throw away. Exact values
          are typed in the paired fields below, because multi-thumb ARIA is
          read inconsistently.
        -->
        <div class="c2c-tl" :class="{ 'c2c-tl--off': effectiveDuration <= 0 }">
          <div class="c2c-tl-rail"></div>
          <!-- What is being kept, drawn as a panel rather than a line, with
               the discarded head and tail dimmed on either side. Round dots
               on a thin rail read as a form control; this reads as film. -->
          <div class="c2c-tl-selection" :style="selectionStyle"></div>
          <div v-if="currentTime > 0" class="c2c-tl-playhead" :style="playheadStyle"></div>
          <!--
            Both inputs span 0..duration. They used to carry narrowed bounds
            (start capped at end-1, end floored at start+1), which meant each
            thumb was positioned as a fraction of its OWN range rather than of
            the timeline: pull the handles together and the start thumb, now
            sitting at its own maximum, jumped to the far right while the end
            thumb sat at its own minimum on the far left. One shared domain
            fixes the geometry; the clamping it was doing lives in the input
            handlers, where it belongs.
          -->
          <input
            :id="startId"
            type="range"
            class="c2c-tl-range"
            :min="0"
            :max="trackMax"
            step="any"
            :value="localStart"
            :style="{ zIndex: startOnTop ? 4 : 3 }"
            :disabled="effectiveDuration <= 0"
            :aria-label="t('trim.start_time_aria')"
            :aria-valuetext="formatTimecode(localStart)"
            @input="onStartInput"
            @change="onStartCommit"
          />
          <input
            :id="endId"
            type="range"
            class="c2c-tl-range"
            :min="0"
            :max="trackMax"
            step="any"
            :value="localEnd"
            :style="{ zIndex: startOnTop ? 3 : 4 }"
            :disabled="effectiveDuration <= 0"
            :aria-label="t('trim.end_time_aria')"
            :aria-valuetext="formatTimecode(localEnd)"
            @input="onEndInput"
            @change="onEndCommit"
          />
        </div>

        <div class="c2c-tl-fields">
          <label class="c2c-tl-field">
            <span>{{ t('trim.start_label') }}</span>
            <input
              type="text"
              class="c2c-tc"
              inputmode="numeric"
              spellcheck="false"
              autocomplete="off"
              :maxlength="timecodeMaxLength"
              :value="startText"
              :placeholder="timecodePlaceholder"
              @beforeinput="onTimecodeBeforeInput"
              @input="onTimecodeInput($event, 'start')"
              @keydown.enter.prevent="commitStartText"
              @blur="commitStartText"
            />
          </label>
          <label class="c2c-tl-field">
            <span>{{ t('trim.end_label') }}</span>
            <input
              type="text"
              class="c2c-tc"
              inputmode="numeric"
              spellcheck="false"
              autocomplete="off"
              :maxlength="timecodeMaxLength"
              :value="endText"
              :placeholder="timecodePlaceholder"
              @beforeinput="onTimecodeBeforeInput"
              @input="onTimecodeInput($event, 'end')"
              @keydown.enter.prevent="commitEndText"
              @blur="commitEndText"
            />
          </label>
        </div>

        <p class="c2c-tl-hint">{{ t('trim.time_format_hint', { example: timecodePlaceholder }) }}</p>
      </div>

      <!-- Only while trimming. The untrimmed length is already on the file
           card and in the player, so a box repeating it was a third copy.
           One line, both edges flush: original on the left, result right. -->
      <dl v-if="trimEnabled" class="c2c-trim-stats">
        <div class="c2c-trim-stat">
          <dt>{{ t('trim.stat_original') }}</dt>
          <dd>{{ formatDuration(effectiveDuration) }}</dd>
        </div>
        <div v-if="trimEnabled && trimActive" class="c2c-trim-stat">
          <dt>{{ t('trim.stat_after') }}</dt>
          <dd>
            {{ formatDuration(outputDuration) }}
            <span class="c2c-trim-removed">{{
              t('trim.stat_removed', { cut: formatDuration(Math.max(0, effectiveDuration - outputDuration)) })
            }}</span>
          </dd>
        </div>
      </dl>

      <div v-if="trimActive" class="c2c-trim-actions">
        <button type="button" class="c2c-trim-reset" @click="reset">
          {{ t('trim.reset') }}
        </button>
      </div>

      <div v-if="outputDuration > MAX_DURATION_SECONDS" class="c2c-trim-warn">
        {{ t('trim.over_limit_after', { duration: formatDuration(outputDuration), limit: formatDuration(MAX_DURATION_SECONDS) }) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, useId } from "vue"
import {
  AudioLines, ChevronDown, ChevronUp, Pause, Play, Scissors, Volume2, VolumeX
} from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import { MAX_DURATION_SECONDS } from "@/lib/config"
import type { TrimRange, MediaMode } from "@/lib/types"

const { t } = useI18n()

const props = defineProps<{
  duration: number
  modelValue: TrimRange | null
  file?: File | null
  mediaMode?: MediaMode
  /** False when the source file carries no audio track, which makes
   *  audio-only impossible and the option is disabled. */
  hasAudio?: boolean
}>()

const emit = defineEmits<{
  "update:modelValue": [TrimRange | null]
  "update:mediaMode": [MediaMode]
  /**
   * Fired when the <video> element loads metadata and reports a duration
   * that's meaningfully different from the duration prop. The parent
   * should patch its own meta.duration so other UI (FileMetadata chips,
   * over-limit warnings) reflects the corrected value. Only emitted
   * when the new duration is finite and > 0; we don't blow away a good
   * prop with a worse number.
   */
  "duration-update": [number]
}>()

const overLimit = computed(() => props.duration > MAX_DURATION_SECONDS)
// The preview is the reason this panel exists, so it starts open and
// the contributor collapses it once they are done looking.
const open = ref(true)
const isAudioOnly = computed(() => props.mediaMode === "audio-only")
const isVideoOnly = computed(() => props.mediaMode === "video-only")

// Use video's actual duration once loaded, falling back to prop
const videoDuration = ref<number>(0)
const effectiveDuration = computed(() => videoDuration.value > 0 ? videoDuration.value : props.duration)

/**
 * The smallest step the handles move in. The readout is whole seconds, so
 * the handles move in whole seconds too; a slider that could land on 16.37
 * while the field said 00:16 is how the two drifted apart before.
 *
 * The ends are exempt, which is the other half of the old bug: a range input
 * only accepts min + n*step, so a 94.7-second clip could never reach its own
 * end and the last second quietly vanished. These sliders carry step="any"
 * and are quantised here instead, where the bounds can be special-cased.
 */
const STEP = 0.1

const localStart = ref<number>(props.modelValue?.start ?? 0)
const localEnd = ref<number>(props.modelValue?.end ?? props.duration)

const trackMax = computed(() => effectiveDuration.value > 0 ? effectiveDuration.value : 100)

/**
 * Which handle catches a pointer where the two overlap. Whichever one is in
 * the far half of the track is the one being reached for, so it goes on top.
 */
const startOnTop = computed(() => localStart.value > effectiveDuration.value / 2)

const minEnd = computed(() => Math.min(localStart.value + STEP, effectiveDuration.value))
const maxStart = computed(() => Math.max(0, localEnd.value - STEP))
const outputDuration = computed(() => Math.max(0, localEnd.value - localStart.value))
const trimActive = computed(() =>
  localStart.value > 0.05 || (effectiveDuration.value - localEnd.value) > 0.05
)

/** Whole seconds, except exactly on a bound, which is always reachable. */
function quantize(v: number, lo: number, hi: number): number {
  if (!(v > lo)) return lo
  if (!(v < hi)) return hi
  if (v - lo < STEP / 2) return lo
  if (hi - v < STEP / 2) return hi
  // A tenth is as fine as a drag can usefully be; the fields below take
  // milliseconds for anyone who needs the exact frame.
  return Math.round(v * 10) / 10
}

/** Trim is opt-in. An upload that arrives with a range already set (a
 *  resumed one, or another batch item) starts with it switched on. */
const trimEnabled = ref(
  !!(props.modelValue && (props.modelValue.start != null || props.modelValue.end != null)))

/** Hours are dropped from the timecode on anything under an hour, so a
 *  90-second clip is not asking people to read 0:01:35. */
const showHours = computed(() => effectiveDuration.value >= 3600)
const timecodePlaceholder = computed(() =>
  showHours.value ? "00:00:00.000" : "00:00.000")
/** mm ss mmm, or hh mm ss mmm, plus the two separators. */
const timecodeDigits = computed(() => showHours.value ? 9 : 7)
const timecodeMaxLength = computed(() => showHours.value ? 12 : 9)

function pad(n: number): string {
  return String(n).padStart(2, "0")
}

function pad3(n: number): string {
  return String(n).padStart(3, "0")
}

/**
 * mm:ss.mmm, or hh:mm:ss.mmm for long sources. Editing tools cut on frames,
 * and a frame at 30fps is 33 ms, so a field that only resolved whole seconds
 * could not express most real cuts. The field is masked rather than free
 * text, which is what kept the milliseconds unusable the first time round.
 */
function formatTimecode(sec: number): string {
  const total = Math.max(0, sec)
  const ms = Math.round(total * 1000)
  const h = Math.floor(ms / 3600000)
  const m = Math.floor((ms % 3600000) / 60000)
  const ss = Math.floor((ms % 60000) / 1000)
  const rest = ms % 1000
  const head = showHours.value
    ? pad(h) + ":" + pad(m) + ":" + pad(ss)
    : pad(m + h * 60) + ":" + pad(ss)
  return head + "." + pad3(rest)
}

/* ------------------------------------------------------------------ *
 *  Timecode entry                                                     *
 * ------------------------------------------------------------------ *
 * Digits are collected and grouped from the right, the way a kitchen
 * timer or a phone stopwatch behaves: 5 is five seconds, 125 is 1:25.
 * The field used to take whatever was typed and hold it verbatim until
 * blur, which is how 00:582342234242332 ended up on screen.
 */

/** Digits only, capped at what the format can hold. */
function timecodeDigitsOf(raw: string): string {
  const cap = timecodeDigits.value
  let out = ""
  for (let i = 0; i < raw.length && out.length < cap; i++) {
    const c = raw.charCodeAt(i)
    if (c >= 48 && c <= 57) out += raw[i]
  }
  return out
}

/**
 * Left to right, the way the placeholder reads: 0016 becomes 00:16, and
 * 0016500 becomes 00:16.500. Same shape as the date field, so the two
 * masked inputs on this form behave identically.
 */
function maskTimecode(d: string): string {
  const cuts = showHours.value ? [2, 4, 6] : [2, 4]
  const seps = showHours.value ? [":", ":", "."] : [":", "."]
  let out = ""
  let last = 0
  for (let i = 0; i < cuts.length && d.length > cuts[i]; i++) {
    out += d.slice(last, cuts[i]) + seps[i]
    last = cuts[i]
  }
  return out + d.slice(last)
}

/** The inverse of the mask. A short fraction pads right: 5 is 500 ms. */
function parseTimecodeDigits(d: string): number | null {
  if (!d) return null
  const headLen = showHours.value ? 6 : 4
  const head = d.slice(0, headLen)
  const frac = d.slice(headLen).padEnd(3, "0")
  let total = 0
  for (let i = 0; i < head.length; i += 2) total = total * 60 + +head.slice(i, i + 2)
  // A head shorter than its format is still whole units of the last group.
  total += +frac / 1000
  return Number.isFinite(total) ? total : null
}

/**
 * What the preview is allowed to play.
 *
 * Trimming to one second and then watching the whole video play through is
 * the preview contradicting the upload. While a trim is set, the seek bar,
 * the play button and the timeupdate guard all work inside this window.
 */
const seekMin = computed(() =>
  trimEnabled.value && trimActive.value ? localStart.value : 0)
const seekMax = computed(() =>
  trimEnabled.value && trimActive.value
    ? localEnd.value
    : (effectiveDuration.value || 100))
const seekSpan = computed(() => Math.max(0.001, seekMax.value - seekMin.value))

/** The player readout: 0:16, 1:35, 1:02:03. Hours only when there are any. */
function clock(sec: number): string {
  const total = Math.max(0, Math.round(sec))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return h > 0 ? h + ":" + pad(m) + ":" + pad(s) : m + ":" + pad(s)
}

/**
 * Both halves of the readout use one unit, decided by the clip.
 * Over a minute it is a clock; under a minute, seconds, because "0:07" for a
 * seven-second clip is more ceremony than information.
 */
const playerTime = computed(() => {
  // While trimming, the readout counts the segment rather than the source:
  // "0:03 / 0:28" of what will be uploaded, not of what was opened.
  const total = seekSpan.value
  const at = Math.max(0, Math.min(total, currentTime.value - seekMin.value))
  if (total >= 60) return clock(at) + " / " + clock(total)
  return Math.round(at) + "s / " + Math.round(total) + "s"
})

const startText = ref("")
const endText = ref("")

watch([localStart, showHours], () => { startText.value = formatTimecode(localStart.value) },
  { immediate: true })
watch([localEnd, showHours], () => { endText.value = formatTimecode(localEnd.value) },
  { immediate: true })

/** Turning trim off puts the range back to the whole video. */
watch(trimEnabled, (on) => {
  if (on) return
  localStart.value = 0
  localEnd.value = effectiveDuration.value
  emitChange()
})

/* ------------------------------------------------------------------ *
 *  Track geometry                                                     *
 * ------------------------------------------------------------------ *
 * A range thumb's centre travels from half a thumb in to half a thumb
 * from the end, never the full width. Anything drawn at a flat percentage
 * of the track therefore runs past the handles at both ends, which is why
 * the highlight used to poke out beyond them. Every overlay below is laid
 * out on the thumb's travel instead.
 */
const THUMB_PX = 14
const INSET = THUMB_PX / 2

/**
 * Clamped 0..1, rendered as a fixed-point string. Number#toString switches
 * to exponent notation below 1e-6, and calc(1e-7 * (100% - 18px)) is simply
 * invalid CSS: the whole declaration is dropped and the overlay jumps to the
 * left edge. Six decimals is finer than a pixel on any plausible track.
 */
function fraction(value: number, total: number): string {
  if (!(total > 0)) return "0"
  const f = value / total
  return (f < 0 ? 0 : f > 1 ? 1 : f).toFixed(6)
}

function atTrack(f: string): string {
  return "calc(" + INSET + "px + " + f + " * (100% - " + THUMB_PX + "px))"
}

function spanTrack(a: string, b: string): string {
  const w = Math.max(0, parseFloat(b) - parseFloat(a)).toFixed(6)
  return "calc(" + w + " * (100% - " + THUMB_PX + "px))"
}

const selectionStyle = computed(() => {
  const total = effectiveDuration.value
  const a = fraction(localStart.value, total)
  const b = fraction(localEnd.value, total)
  return { left: atTrack(a), width: spanTrack(a, b) }
})

const playheadStyle = computed(() => ({
  left: atTrack(fraction(currentTime.value, effectiveDuration.value))
}))

const SEEK_THUMB_PX = 12
const SEEK_INSET = SEEK_THUMB_PX / 2

function spanSeek(a: string, b: string): string {
  const w = Math.max(0, parseFloat(b) - parseFloat(a)).toFixed(6)
  return "calc(" + w + " * (100% - " + SEEK_THUMB_PX + "px))"
}

const seekPlayedStyle = computed(() => ({
  left: SEEK_INSET + "px",
  width: spanSeek("0", fraction(currentTime.value - seekMin.value, seekSpan.value))
}))

/**
 * Digits and the colon only. Letters used to land in the box and sit there
 * until blur; blocking them at beforeinput keeps deletion, selection and
 * paste of valid text working, which a keypress filter would break.
 */
function onTimecodeBeforeInput(e: Event) {
  const data = (e as InputEvent).data
  if (data && /[^0-9:]/.test(data)) e.preventDefault()
}

/**
 * Regroups on every keystroke, so what is on screen is always a timecode.
 * The caret stays at the end: the groups shift right as digits arrive, so
 * there is no stable position to hold it at, and these are five characters.
 */
function onTimecodeInput(e: Event, which: "start" | "end") {
  const el = e.target as HTMLInputElement
  const masked = maskTimecode(timecodeDigitsOf(el.value))
  if (which === "start") startText.value = masked
  else endText.value = masked
  el.value = masked
  el.setSelectionRange(masked.length, masked.length)
}

function commitStartText() {
  const parsed = parseTimecodeDigits(timecodeDigitsOf(startText.value))
  if (parsed == null) { startText.value = formatTimecode(localStart.value); return }
  localStart.value = Math.min(Math.max(0, parsed), maxStart.value)
  startText.value = formatTimecode(localStart.value)
  seekPreviewTo(localStart.value)
  emitChange()
}

function commitEndText() {
  const parsed = parseTimecodeDigits(timecodeDigitsOf(endText.value))
  if (parsed == null) { endText.value = formatTimecode(localEnd.value); return }
  localEnd.value = Math.max(Math.min(effectiveDuration.value, parsed), minEnd.value)
  endText.value = formatTimecode(localEnd.value)
  seekPreviewTo(localEnd.value)
  emitChange()
}

const uid = useId()
const startId = "c2c-trim-start-" + uid
const endId = "c2c-trim-end-" + uid

/* ------------------------------------------------------------------ */
/*  Video player state                                                */
/* ------------------------------------------------------------------ */
const videoEl = ref<HTMLVideoElement | null>(null)
const objectUrl = ref<string | null>(null)
const previewError = ref<string | null>(null)
const isPlaying = ref(false)
const isMuted = ref(false)
const currentTime = ref(0)
let segmentStopHandler: (() => void) | null = null

onMounted(() => {
  if (props.file) createObjectUrl(props.file)
})

onBeforeUnmount(() => {
  revokeObjectUrl()
})

watch(() => props.file, (f) => {
  revokeObjectUrl()
  previewError.value = null
  if (f) createObjectUrl(f)
})

function createObjectUrl(f: File) {
  try { objectUrl.value = URL.createObjectURL(f) }
  catch { previewError.value = t('trim.preview_unavailable') }
}

function revokeObjectUrl() {
  if (objectUrl.value) { URL.revokeObjectURL(objectUrl.value); objectUrl.value = null }
}

function onVideoMeta() {
  const v = videoEl.value
  if (!v) return
  const d = v.duration
  if (Number.isFinite(d) && d > 0) {
    videoDuration.value = d
    // Sync localEnd to actual video duration if props.duration was 0
    if (props.duration <= 0 || localEnd.value <= 0) {
      localEnd.value = d
    }
    // If the upstream prop (from probeFile) was wrong by more than half a
    // second, push the corrected value back so FileMetadata and other
    // siblings can re-render with a real duration. Without this, the user
    // sees "0s" or stale durations on the metadata card while we already
    // know the right number here.
    if (Math.abs(d - props.duration) > 0.5) {
      emit("duration-update", d)
    }
  }
  v.currentTime = localStart.value
}

function onVideoError() {
  previewError.value = t('trim.preview_unsupported')
}

function onTimeUpdate() {
  const v = videoEl.value
  if (!v) return
  // The hard stop. Without it the element plays to the end of the source
  // no matter what the handles say.
  if (!v.paused && v.currentTime >= seekMax.value) {
    v.pause()
    v.currentTime = seekMax.value
  }
  currentTime.value = v.currentTime
}

function togglePlay() {
  const v = videoEl.value
  if (!v || previewError.value) return
  clearSegmentStop()
  if (v.paused) {
    // Pressing play from outside the kept segment starts at its head
    // rather than wherever the playhead happened to be left.
    if (v.currentTime < seekMin.value || v.currentTime >= seekMax.value - 0.02) {
      v.currentTime = seekMin.value
    }
    v.play().catch(() => {})
  } else {
    v.pause()
  }
}

/**
 * Video-only drops the audio track at encode time, so the preview has to
 * stop playing it: hearing sound while the chosen output has none is the
 * preview lying about the upload. Switching back restores it. The mute
 * button still works afterwards as a manual override, because this only
 * fires on a change of mode.
 */
watch(() => props.mediaMode, () => { isMuted.value = isVideoOnly.value },
  { immediate: true })

function toggleMute() {
  // Locked in video-only: the upload will have no audio track, so neither
  // may the preview.
  if (isVideoOnly.value) return
  isMuted.value = !isMuted.value
}

function playSegment() {
  const v = videoEl.value
  if (!v || previewError.value) return
  clearSegmentStop()
  v.currentTime = localStart.value
  v.play().catch(() => {})
  const stop = () => {
    if (v.currentTime >= localEnd.value) {
      v.pause()
      v.removeEventListener("timeupdate", stop)
      segmentStopHandler = null
    }
  }
  segmentStopHandler = stop
  v.addEventListener("timeupdate", stop)
}

function clearSegmentStop() {
  const v = videoEl.value
  if (segmentStopHandler && v) {
    v.removeEventListener("timeupdate", segmentStopHandler)
    segmentStopHandler = null
  }
}

function onSeekInput(e: Event) {
  const v = videoEl.value
  const raw = parseFloat((e.target as HTMLInputElement).value)
  if (!Number.isFinite(raw)) return
  const val = Math.min(Math.max(raw, seekMin.value), seekMax.value)
  // Keep the overlays moving even when the element will not take a seek.
  currentTime.value = val
  if (!v) return
  clearSegmentStop()
  v.currentTime = val
}

/* ------------------------------------------------------------------ */
/*  Slider events                                                     */
/* ------------------------------------------------------------------ */
function emitChange() {
  if (!trimActive.value) { emit("update:modelValue", null); return }
  emit("update:modelValue", {
    start: localStart.value > 0 ? localStart.value : null,
    end: localEnd.value < effectiveDuration.value ? localEnd.value : null
  })
}

function onStartInput(e: Event) {
  const v = parseFloat((e.target as HTMLInputElement).value)
  if (!Number.isFinite(v)) return
  localStart.value = quantize(v, 0, maxStart.value)
  seekPreviewTo(localStart.value)
  emitChange()
}
function onStartCommit() { seekPreviewTo(localStart.value) }

function onEndInput(e: Event) {
  const v = parseFloat((e.target as HTMLInputElement).value)
  if (!Number.isFinite(v)) return
  localEnd.value = quantize(v, minEnd.value, effectiveDuration.value)
  seekPreviewTo(localEnd.value)
  emitChange()
}
function onEndCommit() { seekPreviewTo(localEnd.value) }

function seekPreviewTo(t: number) {
  const v = videoEl.value
  if (!v || previewError.value) return
  clearSegmentStop()
  if (v.paused === false) v.pause()
  const max = Number.isFinite(v.duration) ? v.duration : effectiveDuration.value
  v.currentTime = Math.min(Math.max(0, t), Math.max(0, max - 0.05))
}

function reset() {
  localStart.value = 0
  localEnd.value = effectiveDuration.value
  seekPreviewTo(0)
  emitChange()
}

watch(() => props.modelValue, (v) => {
  localStart.value = v?.start ?? 0
  localEnd.value = v?.end ?? effectiveDuration.value
}, { deep: true })

watch(() => props.duration, (d) => {
  if (d > 0 && videoDuration.value <= 0) {
    localStart.value = 0
    localEnd.value = d
    emitChange()
  }
})

watch(effectiveDuration, (d) => {
  if (d > 0 && localEnd.value <= 0) localEnd.value = d
})

function formatDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "0s"
  if (sec < 60) return sec < 10 ? sec.toFixed(1) + "s" : Math.round(sec) + "s"
  const m = Math.floor(sec / 60)
  const s = Math.round(sec % 60)
  return s === 0 ? m + "m" : m + "m " + s + "s"
}
</script>

<style scoped>
.c2c-trim {
  border: 1px solid #c8ccd1;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.c2c-trim-toggle {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 10px 14px;
  background: #f8f9fa; border: none; cursor: pointer;
  font: inherit; font-size: 14px; font-weight: 600;
  text-align: start; color: #202122;
}
.c2c-trim-toggle:hover { background: #eaf3ff; }
.c2c-trim-toggle:focus-visible { outline: 2px solid #3366cc; outline-offset: -2px; }
.c2c-trim-toggle > span:nth-child(2) { flex: 1 1 auto; }
.c2c-trim-badge {
  font-size: 11px; padding: 2px 8px; border-radius: 10px;
  background: #eaf3ff; color: #2a4b8d; font-weight: 600;
}
.c2c-trim-badge--warn { background: #fef6e7; color: #886600; }

.c2c-trim-body {
  padding: 12px 14px;
  border-top: 1px solid #eaecf0;
  display: flex; flex-direction: column; gap: 12px;
}
.c2c-trim-help { margin: 0; font-size: 12px; color: #54595d; }

.c2c-edit-audio-row {
  display: flex; gap: 16px; padding: 8px 0 4px 0;
  border: none; margin: 0;
}
.c2c-edit-audio-legend {
  font-size: 13px; font-weight: 600;
  color: #54595d; padding: 0; margin-bottom: 6px;
}
.c2c-edit-audio-opt {
  display: flex; align-items: center; gap: 6px;
  font-size: 14px; cursor: pointer;
}
.c2c-edit-audio-opt--disabled { opacity: 0.5; cursor: not-allowed; }
.c2c-edit-audio-note {
  margin: 0 0 4px 0; font-size: 12px; color: #54595d;
}
.c2c-edit-audio-opt input[type="radio"] {
  accent-color: #3366cc;
  width: 16px; height: 16px;
}

/* Video player */
.c2c-trim-preview {
  border-radius: 6px;
  overflow: hidden;
  background: #000;
  border: 1px solid #c8ccd1;
}
.c2c-trim-stage { position: relative; }
.c2c-trim-video {
  width: 100%;
  max-height: 260px;
  display: block;
  object-fit: contain;
  background: #000;
}
/* Audio-only. The cover alone left a hairline of picture showing wherever
   the element's height landed on a fractional pixel, so the element itself
   goes invisible too. opacity rather than display, because the audio track
   has to keep playing through it. */
.c2c-trim-video--hidden { opacity: 0; }

/* Audio-only: say so where the picture used to be. */
.c2c-audio-poster {
  position: absolute; inset: -1px;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 6px;
  padding: 12px; text-align: center;
  background: #3c3f43;
  color: #f8f9fa;
}
.c2c-audio-poster-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 58px; height: 58px; border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}
.c2c-audio-poster strong { font-size: 14px; font-weight: 600; }
.c2c-audio-poster small { font-size: 12px; color: #c8ccd1; max-width: 34ch; line-height: 1.4; }

/* Control bar */
.c2c-video-controls {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  background: #1e1e1e;
}
.c2c-ctrl-btn {
  background: rgba(255,255,255,0.08);
  border: none;
  cursor: pointer;
  padding: 0;
  border-radius: 6px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 30px;
  color: #fff;
  transition: background-color 120ms ease-out;
}
/* Lucide icons are stroked, not filled: they ship fill="none" and draw with
   currentColor. The old rule force-filled every path, which was right for
   Codex and would have turned these into solid blobs. */
.c2c-ctrl-btn :deep(svg) {
  width: 18px; height: 18px;
  display: block;
  color: #fff;
}
.c2c-ctrl-btn:hover:not(:disabled) { background: rgba(255,255,255,0.22); }
.c2c-ctrl-btn--locked,
.c2c-ctrl-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.c2c-ctrl-btn:focus-visible { outline: 2px solid #fff; outline-offset: 1px; }

/* Seek bar. Rail, trim region and played progress are drawn underneath;
   the input contributes only its thumb. */
.c2c-seek-wrap {
  flex: 1 1 auto;
  position: relative;
  height: 22px;
  min-width: 0;
}
.c2c-seek-rail,
.c2c-seek-played {
  position: absolute; top: 50%; transform: translateY(-50%);
  height: 4px; border-radius: 999px; pointer-events: none;
}
.c2c-seek-rail { left: 6px; right: 6px; background: rgba(255,255,255,0.22); }
.c2c-seek-played { background: #7eaaff; }
.c2c-seek-bar {
  position: absolute; inset: 0; width: 100%; height: 100%;
  margin: 0; padding: 0; background: none; border: 0;
  -webkit-appearance: none; appearance: none;
  cursor: pointer;
}
.c2c-seek-bar::-webkit-slider-runnable-track { background: transparent; border: 0; height: 100%; }
.c2c-seek-bar::-moz-range-track { background: transparent; border: 0; height: 100%; }
.c2c-seek-bar::-webkit-slider-thumb {
  -webkit-appearance: none; appearance: none;
  width: 12px; height: 12px; border-radius: 50%;
  background: #fff; border: 0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.5);
}
.c2c-seek-bar::-moz-range-thumb {
  width: 12px; height: 12px; border-radius: 50%;
  background: #fff; border: 0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.5);
}
.c2c-seek-bar:focus { outline: none; }
.c2c-seek-bar:focus-visible::-webkit-slider-thumb { box-shadow: 0 0 0 3px rgba(255,255,255,0.6); }
.c2c-seek-bar:focus-visible::-moz-range-thumb { box-shadow: 0 0 0 3px rgba(255,255,255,0.6); }

.c2c-time-display {
  color: #fff; font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-variant-numeric: tabular-nums;
  white-space: nowrap; flex-shrink: 0;
  text-align: center;
}

.c2c-trim-preview-error {
  margin: 0; padding: 8px 10px;
  background: #54595d; color: #fff; font-size: 12px;
}

/* A 2px-tall text-like control beside the output length read as a label
   rather than something to press. Same size and shape as the other small
   buttons now. */
.c2c-trim-reset {
  background: #fff; border: 1px solid var(--border-color-base, #a2a9b1);
  border-radius: 6px;
  padding: 6px 12px; min-height: 32px;
  font-size: 13px; font-weight: 600; cursor: pointer; color: #202122;
  transition: background-color 120ms ease-out;
}
.c2c-trim-reset:hover { background: var(--background-color-neutral, #eaecf0); }
.c2c-trim-reset:focus-visible {
  outline: 2px solid var(--background-color-progressive, #36c); outline-offset: 1px;
}

.c2c-trim-warn {
  font-size: 12px; padding: 8px 10px;
  background: #fef6e7; color: #886600;
  border: 1px solid #f0d795; border-radius: 4px;
}

@media (max-width: 480px) {
  .c2c-video-controls { flex-wrap: wrap; gap: 4px; }
  .c2c-time-display { font-size: 11px; }
}


/* Screen-reader-only, for the audio group heading. */
.c2c-visually-hidden {
  position: absolute; width: 1px; height: 1px;
  margin: -1px; padding: 0; overflow: hidden;
  clip: rect(0 0 0 0); clip-path: inset(50%); white-space: nowrap; border: 0;
}

.c2c-trim-enable {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; font-weight: 600; color: #202122;
  cursor: pointer; padding: 4px 0;
}
.c2c-trim-enable input { width: 16px; height: 16px; cursor: pointer; }

/* ---------------------------------------------------------------- *
 *  Timeline                                                         *
 * ---------------------------------------------------------------- */
.c2c-trim-editor { display: flex; flex-direction: column; gap: 10px; }

.c2c-tl {
  position: relative; height: 54px;
  padding: 0; margin: 0;
}
.c2c-tl--off { opacity: 0.5; pointer-events: none; }

/* The whole clip: a strip, not a line. The kept span sits on top of it in
   full colour and the discarded head and tail stay grey underneath, which
   is how every editing timeline shows the same thing. */
.c2c-tl-rail {
  position: absolute; top: 50%; transform: translateY(-50%);
  left: 7px; right: 7px; height: 34px;
  border-radius: 6px;
  background:
    repeating-linear-gradient(90deg,
      rgba(0, 0, 0, 0.04) 0 1px, transparent 1px 9px),
    #eaecf0;
  border: 1px solid #dadde1;
  box-sizing: border-box;
  pointer-events: none;
}
.c2c-tl-selection {
  position: absolute; top: 50%; transform: translateY(-50%);
  height: 34px;
  box-sizing: border-box;
  background: rgba(51, 102, 204, 0.18);
  border-top: 2px solid #3366cc;
  border-bottom: 2px solid #3366cc;
  pointer-events: none;
}

.c2c-tl-playhead {
  position: absolute; top: 50%; transform: translate(-50%, -50%);
  width: 2px; height: 40px; border-radius: 1px;
  background: #d73333;
  pointer-events: none;
  z-index: 5;
}

/* Both ranges sit over the same rail; only their thumbs take the pointer. */
.c2c-tl-range {
  position: absolute; inset: 0; width: 100%; height: 100%;
  margin: 0; padding: 0; background: none; border: 0;
  -webkit-appearance: none; appearance: none; pointer-events: none;
}
.c2c-tl-range::-webkit-slider-runnable-track { background: transparent; border: 0; height: 100%; }
.c2c-tl-range::-moz-range-track { background: transparent; border: 0; height: 100%; }
.c2c-tl-range:focus { outline: none; }

/* Grab bars, not dots. Two circles pushed together merged into one blob
   and neither could be told from the other; a pair of bracket-shaped bars
   stays legible edge to edge, which is also what a video editor looks
   like. The grip lines are drawn rather than imported, because a 3px-wide
   decoration inside a pseudo-element cannot take an icon component. */
.c2c-tl-range::-webkit-slider-thumb {
  -webkit-appearance: none; appearance: none;
  pointer-events: auto;
  width: 14px; height: 42px;
  border-radius: 4px;
  background:
    repeating-linear-gradient(90deg,
      rgba(255, 255, 255, 0.85) 0 1px, transparent 1px 4px) center / 7px 14px no-repeat,
    linear-gradient(#3b74d8, #2a5cb8);
  border: 1px solid #22457f;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.32);
  cursor: ew-resize;
}
.c2c-tl-range::-webkit-slider-thumb:active { cursor: grabbing; }
.c2c-tl-range::-moz-range-thumb {
  pointer-events: auto;
  width: 14px; height: 42px;
  border-radius: 4px;
  background:
    repeating-linear-gradient(90deg,
      rgba(255, 255, 255, 0.85) 0 1px, transparent 1px 4px) center / 7px 14px no-repeat,
    linear-gradient(#3b74d8, #2a5cb8);
  border: 1px solid #22457f;
  cursor: ew-resize;
}
.c2c-tl-range:focus-visible::-webkit-slider-thumb {
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.35);
}
.c2c-tl-range:focus-visible::-moz-range-thumb {
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.35);
}

/* Two equal cells that cannot grow past the panel: the old fixed ch width
   plus padding and border overflowed the card. */
.c2c-tl-fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}
.c2c-tl-field { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.c2c-tl-field > span { font-size: 12px; font-weight: 600; color: #54595d; }
.c2c-tc {
  width: 100%; min-width: 0; max-width: 100%;
  box-sizing: border-box;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-variant-numeric: tabular-nums;
  font-size: 14px;
  padding: 8px 10px;
  border: 1px solid var(--border-color-base, #a2a9b1);
  border-radius: 6px;
  background: #fff; color: #202122;
  transition: border-color 120ms ease-out, box-shadow 120ms ease-out;
}
.c2c-tc:focus {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}
.c2c-tl-hint { margin: 0; font-size: 11px; color: var(--color-subtle, #54595d); }

/* One figure per line, labels in a column of their own. Side by side the
   two numbers read as a single sentence and had to be untangled; stacked,
   the comparison is immediate. */
.c2c-trim-stats {
  margin: 4px 0 0 0; padding: 10px 12px;
  display: flex; flex-direction: column; gap: 4px;
  background: #f8f9fa;
  border: 1px solid #eaecf0;
  border-radius: 6px;
  font-size: 13px;
}
.c2c-trim-stat { display: flex; gap: 10px; align-items: baseline; min-width: 0; }
.c2c-trim-stat dt {
  color: var(--color-subtle, #54595d);
  flex: 0 0 auto; min-width: 11ch;
}
.c2c-trim-stat dd {
  margin: 0; font-weight: 600; color: #202122;
  font-variant-numeric: tabular-nums;
}
.c2c-trim-removed { font-weight: 400; color: var(--color-subtle, #54595d); }
.c2c-trim-actions { display: flex; justify-content: flex-end; }
</style>
